import React from 'react';
import { Box, Card, Container, Stack, Typography } from '@mui/material';
import {
  ScheduleOutlined as ClockIcon,
  BoltOutlined as EventIcon,
  TouchAppOutlined as ManualIcon,
  NotificationsActiveOutlined as NudgeIcon,
  ArrowUpwardRounded as EscalateIcon,
  CheckRounded as CheckIcon,
} from '@mui/icons-material';
import { Reveal, useScene, usePrefersReducedMotion } from './motion';
import { nav, status as statusTokens } from '../../theme';

// The teaching section.
//
// The problem it solves: somebody who has never heard of this product read
// six paragraphs and still could not say what it does. So this says it in
// three sentences and draws the rest. The copy is deliberately at the level
// of "a clock, an event, or a person" — no nouns from our domain, nothing
// to look up.
//
// The visual pins while the three steps scroll past it, which is the one
// piece of scroll choreography on the page. Under reduced motion, and on a
// phone, it degrades to each step sitting above its own picture — the same
// information without anything moving.

const STEPS = [
  {
    title: 'Something starts it',
    body: 'A time of day. An event in the operation. Or a person tapping start.',
  },
  {
    title: 'The right person is told',
    body: 'Each step goes to whoever holds that role in that area — with the proof it needs back.',
  },
  {
    title: 'Nothing quietly slides',
    body: 'Late gets a nudge. Still late reaches their manager. You never find out a week on.',
  },
];

// ---------------------------------------------------------------- visuals

const TriggerVisual: React.FC = () => {
  const reduced = usePrefersReducedMotion();
  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    if (reduced) return undefined;
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % 3), 1600);
    return () => window.clearInterval(timer);
  }, [reduced]);

  const items = [
    { icon: <ClockIcon />, label: '06:00', sub: 'Every weekday' },
    { icon: <EventIcon />, label: 'Van out of service', sub: 'Reported from the road' },
    { icon: <ManualIcon />, label: 'Start onboarding', sub: 'Tapped by a manager' },
  ];

  return (
    <Stack gap={1.5}>
      {items.map((item, i) => {
        const on = reduced || i === index;
        return (
          <Card
            key={item.label}
            sx={{
              p: 2, display: 'flex', alignItems: 'center', gap: 1.75,
              borderColor: on ? 'primary.main' : 'divider',
              bgcolor: on ? 'primary.light' : 'background.paper',
              transform: on ? 'translate3d(0,0,0)' : 'translate3d(0,0,0) scale(0.985)',
              transition: reduced ? 'none' : 'all 420ms cubic-bezier(0.22,1,0.36,1)',
            }}
          >
            <Box sx={{ color: on ? 'primary.dark' : 'text.disabled', display: 'flex' }}>{item.icon}</Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{item.label}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap>{item.sub}</Typography>
            </Box>
          </Card>
        );
      })}
    </Stack>
  );
};

const AssignVisual: React.FC = () => (
  <Card sx={{ p: 2.5 }}>
    <Typography variant="overline" color="text.disabled" display="block" sx={{ mb: 0.5 }}>
      Dana W. · Driver · Area 3
    </Typography>
    <Typography variant="h5" sx={{ mb: 0.5 }}>Start-of-shift check</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5 }}>
      Van 214 · due 07:30
    </Typography>

    <Stack gap={1.5}>
      {[['Walk-around and tyres', true], ['Photograph any damage', true], ['Load matches manifest', false]].map(
        ([label, done]) => (
          <Stack key={String(label)} direction="row" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                display: 'grid', placeItems: 'center',
                border: '2px solid',
                borderColor: done ? statusTokens.ok : statusTokens.warn,
                bgcolor: done ? statusTokens.ok : 'transparent',
                color: '#fff',
              }}
            >
              {done ? <CheckIcon sx={{ fontSize: 12 }} /> : null}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: done ? 'text.disabled' : 'text.primary',
                textDecoration: done ? 'line-through' : 'none',
                fontWeight: done ? 400 : 600,
              }}
            >
              {label}
            </Typography>
          </Stack>
        ),
      )}
    </Stack>
  </Card>
);

const EscalateVisual: React.FC = () => (
  <Stack gap={1.5}>
    <Card sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.75 }}>
      <Box sx={{ color: statusTokens.warn, display: 'flex' }}><NudgeIcon /></Box>
      <Box>
        <Typography variant="body2" fontWeight={600}>07:30 · Reminder sent</Typography>
        <Typography variant="caption" color="text.secondary">Dana W. — one step outstanding</Typography>
      </Box>
    </Card>

    <Box sx={{ pl: 3, color: 'text.disabled', display: 'flex', alignItems: 'center', gap: 1 }}>
      <EscalateIcon sx={{ fontSize: 16, transform: 'rotate(180deg)' }} />
      <Typography variant="caption">22 minutes, no answer</Typography>
    </Box>

    <Card
      sx={{
        p: 2, display: 'flex', alignItems: 'center', gap: 1.75,
        borderColor: 'error.light', bgcolor: 'error.light',
      }}
    >
      <Box sx={{ color: 'error.dark', display: 'flex' }}><EscalateIcon /></Box>
      <Box>
        <Typography variant="body2" fontWeight={700} sx={{ color: 'error.dark' }}>
          07:52 · Escalated to manager
        </Typography>
        <Typography variant="caption" sx={{ color: 'error.dark' }}>
          Now on their screen, not lost
        </Typography>
      </Box>
    </Card>
  </Stack>
);

const VISUALS = [<TriggerVisual />, <AssignVisual />, <EscalateVisual />];

// ---------------------------------------------------------------- section

// Roughly half a screen of scroll per beat. It was a full screen, which
// meant three flicks of the wheel to get from "something starts it" to the
// picture that explains it.
const BEAT_VH = 75;

const HowItWorks: React.FC = () => {
  const reduced = usePrefersReducedMotion();
  const { ref, index, progress } = useScene<HTMLDivElement>(STEPS.length);

  const heading = (
    <Box>
      <Typography variant="overline" sx={{ color: nav.accent, display: 'block', mb: 1.5 }}>
        How it works
      </Typography>
      <Typography variant="h2" sx={{ color: '#fff', maxWidth: 520 }}>
        One engine, running every routine you have.
      </Typography>
    </Box>
  );

  // Under reduced motion, and on a phone, the scene is just the three steps
  // stacked with their own pictures. Nothing pins, nothing fades.
  const stacked = (
    <Stack gap={{ xs: 6, md: 8 }}>
      {STEPS.map((step, i) => (
        <Box key={step.title}>
          <Stack direction="row" gap={2.5} alignItems="flex-start" sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800, lineHeight: 1.9, color: nav.accent, flexShrink: 0 }}>
              0{i + 1}
            </Typography>
            <Box>
              <Typography variant="h3" sx={{ color: '#fff', mb: 1.25 }}>{step.title}</Typography>
              <Typography sx={{ color: nav.text, fontSize: 17, lineHeight: 1.65, maxWidth: 440 }}>
                {step.body}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ maxWidth: 420 }}>{VISUALS[i]}</Box>
        </Box>
      ))}
    </Stack>
  );

  return (
    <Box sx={{ bgcolor: nav.bg, color: '#fff' }}>
      {/* --------------------------------------------------- small screens */}
      <Container
        maxWidth="lg"
        sx={{ display: { xs: 'block', md: reduced ? 'block' : 'none' }, py: { xs: 7, md: 10 } }}
      >
        <Reveal sx={{ mb: 6 }}>{heading}</Reveal>
        {stacked}
      </Container>

      {/* ------------------------------------------------------- the scene */}
      {/* The heading lives INSIDE the pinned row. It used to sit above the
          scene and scroll away, so by step 02 there was nothing on screen
          saying what you were looking at. */}
      <Box
        ref={ref}
        data-scene="how-it-works"
        sx={{
          display: { xs: 'none', md: reduced ? 'none' : 'block' },
          position: 'relative',
          height: `${STEPS.length * BEAT_VH}vh`,
        }}
      >
        <Box sx={{ position: 'sticky', top: 0, height: '100vh', display: 'flex', alignItems: 'center' }}>
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                columnGap: 8,
                rowGap: 5,
                alignItems: 'center',
              }}
            >
              {/* Pinned with everything else, top-left of the row. */}
              <Box sx={{ gridColumn: '1 / -1' }}>{heading}</Box>

              {/* ----------------------------------------------- the copy */}
              <Stack direction="row" gap={4}>
                <Stack gap={1.25} sx={{ pt: 1.25 }}>
                  {STEPS.map((step, i) => (
                    <Box
                      key={step.title}
                      sx={{
                        width: 2, height: 34, borderRadius: 1,
                        bgcolor: i === index ? nav.accent : 'rgba(255,255,255,0.16)',
                        transition: 'background-color 320ms',
                      }}
                    />
                  ))}
                </Stack>

                <Box sx={{ display: 'grid', flex: 1 }}>
                  {STEPS.map((step, i) => (
                    <Box
                      key={step.title}
                      aria-hidden={i !== index}
                      data-panel="copy"
                      sx={{
                        gridArea: '1 / 1',
                        alignSelf: 'center',
                        opacity: i === index ? 1 : 0,
                        // Scroll-linked drift on top of the fade, so the
                        // panel moves WITH the wheel rather than only
                        // snapping when the beat changes.
                        transform: `translate3d(0, ${
                          i === index ? (progress - i) * -22 : 16
                        }px, 0)`,
                        pointerEvents: i === index ? 'auto' : 'none',
                        transition: 'opacity 340ms cubic-bezier(0.22,1,0.36,1)',
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 13, fontWeight: 800, color: nav.accent, mb: 1.25, letterSpacing: '0.04em' }}
                      >
                        0{i + 1}
                      </Typography>
                      <Typography variant="h3" sx={{ color: '#fff', mb: 1.5 }}>{step.title}</Typography>
                      <Typography sx={{ color: nav.text, fontSize: 17, lineHeight: 1.6, maxWidth: 420 }}>
                        {step.body}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Stack>

              {/* --------------------------------------------- the picture */}
              <Box sx={{ display: 'grid', width: '100%', maxWidth: 420, justifySelf: 'end' }}>
                {VISUALS.map((visual, i) => (
                  <Box
                    key={i}
                    aria-hidden={i !== index}
                    data-panel="picture"
                    sx={{
                      gridArea: '1 / 1',
                      alignSelf: 'center',
                      opacity: i === index ? 1 : 0,
                      transform: `translate3d(0, ${i === index ? (progress - i) * -22 : 16}px, 0)`,
                      pointerEvents: i === index ? 'auto' : 'none',
                      transition: 'opacity 340ms cubic-bezier(0.22,1,0.36,1)',
                    }}
                  >
                    {visual}
                  </Box>
                ))}
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default HowItWorks;
